import type { Activity, AdminGame } from '@mcgames/contracts';
import { Spinner } from '@mcgames/ui';
import { useTranslation } from 'react-i18next';
import { HttpError } from '../api/http.ts';
import { useApiQuery } from '../api/hooks.ts';
import { useFormatBytes } from '../lib/format-bytes.ts';
import styles from './Admin.module.css';
import { GAMES_PATH } from './games/game-hooks.ts';

const ACTIVITY_PATH = '/api/admin/activity';
const POLL_MS = 2000;

export function ActivityPage() {
  const { t } = useTranslation('admin');
  // The poll is the retry, so a failed request is not retried on top of it. An error does not stop
  // the polling, which is how the page recovers once the Content API is back.
  const activity = useApiQuery<Activity>(ACTIVITY_PATH, {
    refetchInterval: POLL_MS,
    retry: false,
  });
  const games = useApiQuery<AdminGame[]>(GAMES_PATH);
  const titles = new Map(games.data?.map((game) => [game.id, game.title]));

  return (
    <div className={styles['page']}>
      <h1 className={styles['heading']}>{t('activity.title')}</h1>
      <p className={styles['muted']}>{t('activity.intro')}</p>
      {activity.isPending && <Spinner />}
      {activity.isError && (
        <p role="alert" className={styles['error']}>
          {isContentApiDown(activity.error) ? t('activity.down') : t('activity.loadFailed')}
        </p>
      )}
      {activity.isSuccess && (
        <>
          <NntpUsage nntp={activity.data.nntp} />
          <Downloads downloads={activity.data.downloads} titles={titles} />
        </>
      )}
    </div>
  );
}

function isContentApiDown(error: Error): boolean {
  return error instanceof HttpError && error.code === 'UPSTREAM_ERROR';
}

function NntpUsage({ nntp }: { nntp: Activity['nntp'] }) {
  const { t } = useTranslation('admin');
  const share = nntp.maxConnections > 0 ? Math.min(1, nntp.inUse / nntp.maxConnections) : 0;
  return (
    <section className={styles['card']}>
      <div className={styles['meterHeader']}>
        <h2 className={styles['subheading']}>{t('activity.nntp')}</h2>
        <span className={styles['meterValue']}>
          {t('activity.nntpUsage', { inUse: nntp.inUse, max: nntp.maxConnections })}
        </span>
      </div>
      <div
        className={styles['track']}
        role="progressbar"
        aria-label={t('activity.nntp')}
        aria-valuemin={0}
        aria-valuemax={nntp.maxConnections}
        aria-valuenow={nntp.inUse}
        aria-valuetext={t('activity.nntpUsage', { inUse: nntp.inUse, max: nntp.maxConnections })}
      >
        <div className={styles['fill']} style={{ width: `${Math.round(share * 100)}%` }} />
      </div>
    </section>
  );
}

function Downloads({
  downloads,
  titles,
}: {
  downloads: Activity['downloads'];
  titles: Map<string, string>;
}) {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const formatBytes = useFormatBytes();

  if (downloads.length === 0) return <p className={styles['muted']}>{t('activity.empty')}</p>;
  return (
    <div className={styles['tableWrap']}>
      <table className={styles['table']} aria-label={t('activity.downloads')}>
        <thead>
          <tr>
            <th scope="col">{t('activity.user')}</th>
            <th scope="col">{t('activity.game')}</th>
            <th scope="col">{t('activity.file')}</th>
            <th scope="col">{t('activity.speed')}</th>
          </tr>
        </thead>
        <tbody>
          {downloads.map((download) => (
            <tr key={`${download.userId}:${download.gameId}:${download.fileName}`}>
              <td className={styles['wrap']}>{download.username}</td>
              <td className={styles['wrap']}>{titles.get(download.gameId) ?? download.gameId}</td>
              <td className={styles['wrap']}>{download.fileName}</td>
              <td className={styles['nowrap']}>
                {tCommon('units.perSecond', { value: formatBytes(download.bytesPerSecond) })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
